import { Vector2 } from 'three';
import { Stage } from './Stage.js';
import { Bus } from './Bus.js';
import { CameraRig } from './CameraRig.js';
import { Inspector } from './Inspector.js';
import { sortedLayers } from './Registry.js';
import { Helix } from '../geometry/spiral.js';
import { Palette } from '../config/palette.js';
import { resolveSettings } from '../config/settings.js';
import { Journey } from '../interaction/Journey.js';
import { Pointer } from '../interaction/Pointer.js';
import { Gaze } from '../interaction/Gaze.js';
import { AudioEngine } from '../audio/AudioEngine.js';
import { Overlay } from '../ui/Overlay.js';

/**
 * @typedef {object} FrameState
 * @property {number} t         journey position, smoothed, 0 at the top landing
 * @property {number} tTarget   where the traveller is trying to be
 * @property {number} velocity  d(t)/dt
 * @property {number} dt        clamped seconds since the last frame
 * @property {number} elapsed   seconds since the city opened
 * @property {Vector2} pointer  smoothed, in NDC
 * @property {boolean} holding  pointer or space held down
 * @property {number} iris      0 on the stair, 1 with an eye to a telescope
 * @property {number} pixelRatio
 */

/**
 * @typedef {object} Context
 * @property {import('three').Scene} scene
 * @property {import('three').PerspectiveCamera} camera
 * @property {Stage} stage
 * @property {Bus} bus
 * @property {Palette} palette
 * @property {Helix} helix
 * @property {Gaze} gaze
 * @property {AudioEngine} audio
 * @property {ReturnType<typeof resolveSettings>} settings
 * @property {Array<import('../layers/Layer.js').Layer>} layers
 */

/**
 * Isidora, assembled.
 *
 * The App owns nothing of the city itself. It builds the apparatus, hands
 * every layer the same context, and then does the same six things every
 * frame: move the traveller, age the palette, carry the camera, work out
 * what is being looked at, tick the layers, draw.
 */
export class App {
  /** @param {HTMLCanvasElement} canvas @param {HTMLElement} overlayRoot */
  constructor(canvas, overlayRoot) {
    this.canvas = canvas;
    this.settings = resolveSettings();
    this.bus = new Bus();
    this.palette = new Palette();
    this.stage = new Stage(canvas, this.settings);

    const s = this.settings.stair;
    this.helix = new Helix({
      radius: (s.innerRadius + s.outerRadius) / 2,
      turns: s.turns,
      height: s.height,
      topY: 0,
      phase: 0,
      handed: 1,
    });

    /** @type {Context} */
    this.ctx = {
      scene: this.stage.scene,
      camera: this.stage.camera,
      stage: this.stage,
      bus: this.bus,
      palette: this.palette,
      helix: this.helix,
      settings: this.settings,
      layers: [],
      gaze: null,
      audio: null,
    };

    this.ctx.gaze = new Gaze(this.ctx);
    this.ctx.audio = new AudioEngine(this.ctx);

    this.rig = new CameraRig(this.stage.camera, this.helix, this.settings);
    this.journey = new Journey(canvas, this.settings.journey, this.bus);
    this.pointer = new Pointer(canvas);
    this.overlay = new Overlay(overlayRoot, this.ctx);
    this.inspector = new Inspector(this.ctx);

    /** @type {FrameState} */
    this.state = {
      t: 0,
      tTarget: 0,
      velocity: 0,
      dt: 0,
      elapsed: 0,
      pointer: new Vector2(),
      holding: false,
      iris: 0,
      pixelRatio: 1,
    };

    this.holdingKey = false;
    this.arrivalFlash = 0;
    this.running = false;
    this.elapsed = 0;
    this._last = 0;
    this._bound = [];

    this._wireInput();
  }

  _wireInput() {
    const on = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts);
      this._bound.push(() => target.removeEventListener(type, fn, opts));
    };

    on(globalThis, 'keydown', (e) => {
      if (e.code === 'Space' || e.key === 'Enter') this.holdingKey = true;
      if (e.key === 'm' || e.key === 'M') this._toggleSound();
    });
    on(globalThis, 'keyup', (e) => {
      if (e.code === 'Space' || e.key === 'Enter') this.holdingKey = false;
    });
    on(globalThis, 'blur', () => (this.holdingKey = false));
    on(globalThis, 'resize', () => this._resize());
    on(document, 'visibilitychange', () => {
      // Coming back from another tab must not teleport the traveller.
      this._last = performance.now();
    });

    this.bus.on('ui:sound-toggle', () => this._toggleSound());
    this.bus.on('arrive', () => (this.arrivalFlash = 1));

    // The telescopes ask for the rig; the rig is what actually lends it.
    this.bus.on('telescope:enter', ({ eye }) => this.rig.borrow(eye));
    this.bus.on('telescope:exit', () => this.rig.borrow(null));

    this.ctx.audio.arm(this.canvas);
  }

  _toggleSound() {
    const audio = this.ctx.audio;
    if (!audio.ready) {
      audio.unlock().then(() => this.overlay.setSound(true));
      return;
    }
    audio.setMuted(!audio.muted);
    this.overlay.setSound(!audio.muted);
  }

  async build() {
    for (const Klass of sortedLayers()) {
      const layer = new Klass(this.ctx);
      await layer.build();
      layer.built = true;
      this.stage.scene.add(layer.group);
      this.ctx.layers.push(layer);
    }
    this.layers = this.ctx.layers;
    // Where the descent ends up looking. The Elders layer knows where the
    // wall is; the rig only needs the point.
    if (this.ctx.arrival) this.rig.arrival = this.ctx.arrival;
    this._resize();
  }

  _resize() {
    const { width, height, dpr } = this.stage.resize();
    this.rig.baseFov = this.stage.baseFov;
    this.state.pixelRatio = dpr;
    for (const layer of this.layers) layer.resize(width, height);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._last = performance.now();
    this.overlay.lift();
    const loop = (now) => {
      if (!this.running) return;
      this._frame(now);
      this._raf = requestAnimationFrame(loop);
    };
    this._raf = requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this._raf);
  }

  _frame(now) {
    // A long stall (a tab in the background, a GC pause) must not be
    // integrated: clamp, so the traveller never falls down the stair.
    const dt = Math.min((now - this._last) / 1000, 1 / 20);
    this._last = now;
    this.elapsed += dt;

    const state = this.state;
    state.dt = dt;
    state.elapsed = this.elapsed;

    // 1 · the traveller moves
    state.t = this.journey.update(dt);
    state.tTarget = this.journey.target;
    state.velocity = this.journey.velocity;
    this.pointer.update(dt);
    state.pointer.copy(this.pointer.smooth);
    state.holding = this.pointer.down || this.holdingKey;

    // 2 · the hour turns
    this.palette.setJourney(state.t);

    // 3 · the body is carried
    state.iris = this.rig.update(dt, state.t, state.pointer, state.velocity, this.elapsed);

    // 4 · attention lands somewhere
    this.ctx.gaze.update(dt, state);

    // 5 · the city answers
    for (const layer of this.layers) {
      if (layer.enabled) layer.update(dt, state);
    }
    this.ctx.audio.update(dt, state);

    // 6 · it is drawn
    this._applyPost(state);
    this.overlay.update(state);
    const text = this.inspector.update(dt, state);
    this.overlay.setInspector(this.inspector.on, text ?? '');
    this.stage.render(this.elapsed);
  }

  _applyPost(state) {
    const { composite, bloom, renderer } = this.stage;
    const post = this.settings.post;
    const u = composite.uniforms;

    u.uIris.value = state.iris;
    // The image starts to remember itself: the bleed grows with the descent
    // and spikes at the moment of arrival.
    u.uBleed.value = Math.pow(state.t, 2.2) * 0.3;
    u.uVignette.value = post.vignette + state.t * 0.1;
    u.uGrain.value = post.grain * (1 + state.t * 0.55);
    const fog = this.palette.get('fog');
    u.uTint.value.set(fog.r, fog.g, fog.b);

    // Arrival: one slow flare, then the night takes it back. It has to decay
    // on its own clock — keyed to `t` it would never end, and the last page
    // of the descent would sit under a permanent warm veil.
    this.arrivalFlash *= Math.exp(-state.dt * 0.5);
    if (this.arrivalFlash < 1e-4) this.arrivalFlash = 0;
    u.uFlash.value = this.arrivalFlash * 0.16;

    bloom.strength = post.bloomStrength + state.t * 0.26 + state.iris * 0.18;
    // An eyepiece loses light. Opening the instrument should feel like the
    // pupil adjusting, not like staring into a lamp.
    renderer.toneMappingExposure = post.exposure * (1 - state.t * 0.16) * (1 - state.iris * 0.3);
  }

  dispose() {
    this.stop();
    this._bound.forEach((off) => off());
    for (const layer of this.layers ?? []) layer.dispose();
    this.overlay.dispose();
    this.journey.dispose();
    this.pointer.dispose();
    this.ctx.audio.dispose();
    this.stage.dispose();
  }
}
